import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Table from "@components/layout/Table";
import ApprovalTag from "@components/shared/ApprovalTag";
import Label from "@components/form/Label";
import SearchInput from "@components/form/SearchInput";
import SeasonalAssignmentModal from "@components/modal/SeasonalAssignmentModal";
import SelectAdminApprovalStatus from "@components/form/SelectAdminApprovalStatus";
import SelectLevel1Resource from "@components/form/SelectLevel1Resource";
import SelectAccountingPeriod from "@components/form/SelectAccountingPeriod";
import { useAllAdministrativeApprovals } from "@hooks/query/useAllAdministrativeApprovals";
import { getActionTimestamp } from "@utils/administrativeApproval";
import { formatDate } from "@utils/formatDate";
import { formatVolume } from "@utils/formatVolume";
import { AdministrativeApprovalType } from "@services/administrativeApprovals";
import { formatAccountingPeriod } from "@utils/formatAccountingPeriod";
import { useGetAdministrativeApproval } from "@hooks/query/useGetAdministrativeApproval";
import { useAppContext } from "@context/AppContext";
import NoteIndicatorBadge from "@components/notes/NoteIndicatorBadge";
import RowNotesModal from "@components/notes/RowNotesModal";
import { useNoteCounts } from "@hooks/query/useNoteCounts";
import { DBTables } from "@utils/constants/dbTables";

type SeasonalTransferTableProps = {
  level1Resource?: any;
};

const SeasonalTransferTable: React.FunctionComponent<
  SeasonalTransferTableProps
> = ({ level1Resource }) => {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState("");
  const [openNotesRow, setOpenNotesRow] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const { checkPermissions } = useAppContext();
  const { data: approval } = useGetAdministrativeApproval(selectedId);
  const [filter, setFilter] = useState<{
    accountingPeriod?: {
      id: string;
      periodStart?: Date;
      periodEnd?: Date;
    };
    customerName?: string;
    status?: string;
    level1wrsId?: string;
  }>({ accountingPeriod: level1Resource?.activeAccountingPeriod });

  const { data: seasonalAssignments = [], isLoading } =
    useAllAdministrativeApprovals({
      params: {
        type: AdministrativeApprovalType.SWT,
      },
    });

  const approvalIds = seasonalAssignments.map((a: any) => a.id);
  const { data: noteCounts = {} } = useNoteCounts(
    DBTables.AdministrativeApproval,
    approvalIds,
  );
  const canViewNotes = checkPermissions(["ViewNotes"]);

  const handleFilterChange = (field: string, value: any) => {
    setFilter({
      ...filter,
      [field]: value,
    });
  };

  return (
    <>
      <header className="flex justify-between items-end gap-3">
        <form className="grid grid-cols-1 gap-2 md:grid-cols-4 md:gap-3 2xl:w-2/1 relative z-20 justify-between items-end">
          {!level1Resource && (
            <div>
              <Label htmlFor="level1Resource">{t("common.level1wrs")}</Label>
              <SelectLevel1Resource
                inputId="level1Resource"
                onChange={e => {
                  setFilter(prev => ({
                    ...prev,
                    level1wrsId: e?.value,
                    accountingPeriod: { id: "" },
                  }));
                }}
                isClearable
              />
            </div>
          )}

          {level1Resource || filter.level1wrsId ? (
            <div>
              <Label htmlFor="accountingPeriod">
                {t("common.accounting_period")}
              </Label>
              <SelectAccountingPeriod
                key={level1Resource?.id || filter.level1wrsId}
                inputId="accountingPeriod"
                level1ResourceId={level1Resource?.id || filter.level1wrsId}
                value={filter.accountingPeriod}
                onChange={(e: any) => {
                  handleFilterChange("accountingPeriod", {
                    id: e?.value?.id || "",
                    periodStart:
                      e?.value?.periodStart && new Date(e?.value.periodStart),
                    periodEnd:
                      e?.value?.periodEnd && new Date(e?.value.periodEnd),
                  });
                }}
              />
            </div>
          ) : null}
          <div>
            <Label htmlFor="customer-name">
              {t("subscriber.filter_subscriber_name")}
            </Label>
            <SearchInput
              id="customer-name"
              value={filter.customerName}
              onChange={e => handleFilterChange("customerName", e.target.value)}
            />
          </div>
          <div>
            <Label>{t("common.status")}</Label>
            <SelectAdminApprovalStatus
              onValueChange={value => handleFilterChange("status", value)}
              adminApprovalsAndTransfers={seasonalAssignments}
            />
          </div>
        </form>
        {checkPermissions(["CreateApprovals"]) && (
          <Link
            to={`/polestar/administrative_approvals/seasonal_transfer${
              level1Resource ? `?level1ResourceId=${level1Resource.id}` : ""
            }`}
            className="btn-secondary text-sm rounded whitespace-nowrap"
          >
            {t("approval.seasonal_water_assignments.add_new")}
          </Link>
        )}
      </header>
      <Table
        fields={[
          {
            title: t("common.level1wrs"),
            name: "level1Resource",
          },
          {
            title: t("common.accounting_period"),
            name: "accountingPeriod",
          },
          {
            title: t("approval.seasonal_water_assignments.from_subscriber"),
            name: "sellerName",
          },
          {
            title: t("approval.seasonal_water_assignments.to_subscriber"),
            name: "buyerName",
          },
          {
            title: t("common.volume"),
            name: "volume",
          },
          {
            title: t("common.status"),
            name: "status",
          },
          {
            title: t("approval.seasonal_water_assignments.timestamp"),
            name: "timestamp",
          },
          {
            title: t("common.effective_date"),
            name: "effectiveDate",
          },
          ...(canViewNotes
            ? [
                {
                  title: t("subscriber.notes"),
                  name: "notes",
                },
              ]
            : []),
          {
            title: t("common.action"),
            name: "action",
          },
        ]}
        data={seasonalAssignments
          ?.filter(
            (row: { level1Resource: any }) =>
              level1Resource === undefined ||
              row.level1Resource?.id === level1Resource?.id,
          )
          ?.filter(
            (row: { level1Resource: any }) =>
              filter.level1wrsId === undefined ||
              row.level1Resource?.id === filter.level1wrsId,
          )
          ?.filter(
            (row: { accountingPeriod: any }) =>
              !filter.accountingPeriod?.id ||
              row.accountingPeriod?.id === filter.accountingPeriod?.id,
          )
          ?.filter(
            (row: { status: any }) =>
              filter.status === undefined || row.status === filter.status,
          )
          ?.filter(
            (row: { seller: any; buyer: any }) =>
              !filter?.customerName?.length ||
              row.seller?.name
                .toString()
                .toLowerCase()
                .includes(filter?.customerName.toString().toLowerCase()) ||
              row.buyer?.name
                .toString()
                .toLowerCase()
                .includes(filter?.customerName.toString().toLowerCase()),
          )
          ?.map((app: any) => {
            return {
              ...app,
              notes: (
                <NoteIndicatorBadge
                  count={noteCounts[app.id]}
                  onClick={() =>
                    setOpenNotesRow({
                      id: app.id,
                      label: `${app?.seller?.name ?? ""} → ${app?.buyer?.name ?? ""}`,
                    })
                  }
                />
              ),
              level1Resource: app.level1Resource.identifier,
              createdAt: formatDate(new Date(app.lodgedAt)),
              effectiveDate: app.effectiveDate
                ? formatDate(new Date(app.effectiveDate))
                : "-",
              sellerName: app.seller.name,
              buyerName: app.buyer.name,
              volume: formatVolume(app.volume),
              accountingPeriod: formatAccountingPeriod(app.accountingPeriod),
              status: (
                <ApprovalTag
                  isAccounted={Boolean(app?.accountedAt)}
                  status={app?.status}
                />
              ),
              timestamp: getActionTimestamp(app),
              action: (
                <div className="flex gap-1 items-center w-full">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedId(app.id);
                    }}
                    className="btn-default text-sm"
                  >
                    {t("common.view")}
                  </button>
                </div>
              ),
            };
          })}
        stickyHeader
        loading={isLoading}
        noRowsText={t("common.no_data")}
      />
      {selectedId && approval ? (
        <SeasonalAssignmentModal
          data={approval}
          onClose={() => {
            setSelectedId("");
          }}
        />
      ) : null}
      <RowNotesModal
        open={openNotesRow !== null}
        referenceTable={DBTables.AdministrativeApproval}
        referenceId={openNotesRow?.id ?? ""}
        artifactName={openNotesRow?.label}
        onClose={() => setOpenNotesRow(null)}
      />
    </>
  );
};

export default SeasonalTransferTable;
